import React, { useMemo } from 'react';
import { Descriptions, Modal, Tag } from 'antd';

interface DescriptionItem {
  label: string;
  value: any;
}

const MalfunctionDebugModal = ({
  data,
  isModalVisible,
  setIsModalVisible,
}: {
  data: any;
  isModalVisible: boolean | undefined;
  setIsModalVisible(isModalVisible: boolean): void;
}) => {
  const debugInfo: string | null = data?.debug_info || null;

  const parsedData = useMemo(() => {
    if (!debugInfo || typeof debugInfo !== 'string') return null;

    const jsonMatch = debugInfo.match(/{.*}/);
    const jsonPart = jsonMatch ? jsonMatch[0] : null;

    let jsonObject = {};
    if (jsonPart) {
      try {
        jsonObject = JSON.parse(jsonPart);
      } catch (e) {
        console.error('JSON parsing error:', e);
      }
    }

    const prefixPart = debugInfo.replace(jsonPart || '', '');
    const entries = prefixPart
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);

    const info: Record<string, string> = {};
    entries.forEach((entry) => {
      const [key, ...rest] = entry.split(':');
      if (key && rest.length > 0) {
        info[key.trim()] = rest.join(':').trim();
      }
    });

    return {
      ...info,
      ...(typeof jsonObject === 'object' && !Array.isArray(jsonObject) ? jsonObject : {}),
    };
  }, [debugInfo]);

  // flatten for descriptions
  const generateItems = (data: any): DescriptionItem[] => {
    if (!data) return [];

    const items: DescriptionItem[] = [];

    const flattenObject = (obj: any, prefix: string = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const label = prefix ? `${prefix} → ${key}` : key;

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          flattenObject(value, label);
        } else {
          items.push({ label, value });
        }
      });
    };

    flattenObject(data);
    return items;
  };

  const renderValue = (value: any) => {
    if (typeof value === 'boolean') {
      return <Tag color={value ? 'geekblue' : 'red'}>{value ? 'True' : 'False'}</Tag>;
    }
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'object') {
      try {
        return <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  };

  const handleCancel = () => setIsModalVisible(false);

  return (
    <Modal
      title="Malfunction Debug Info"
      open={isModalVisible}
      footer={null}
      onCancel={handleCancel}
      width={800}
    >
      {parsedData ? (
        <Descriptions bordered column={1} size="small">
          {generateItems(parsedData).map((item, index) => (
            <Descriptions.Item key={index} label={item.label}>
              {renderValue(item.value)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <p>No valid debug info.</p>
      )}

      <p>{debugInfo}</p>
    </Modal>
  );
};

export default MalfunctionDebugModal;
